/* import __COLOCATED_TEMPLATE__ from './profile-overlay-user.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task, restartableTask, waitForProperty } from 'ember-concurrency';

import {
  SELLER_PROFILE__CACHE_TIMEOUT_MS,
  PROFILE_USER_INCLUDES_BUYER,
  PROFILE_USER_INCLUDES_SELLER,
  PROFILE_USER_INCLUDES_SELF,
} from '../constants/components/profile';

class ProfileOverlayUserComponent extends Component {
  @service('commands/user-commands')
  userCommandService;

  @service('error-tracker')
  errorTracker;

  @service
  session;

  @service
  store;

  @tracked
  project = null;

  get isAnyProfileLoading() {
    return this.manageProfile.isRunning;
  }

  get managedUser() {
    return this.manageProfile.last?.value ?? null;
  }

  constructor() {
    super(...arguments);
    this.userWatcher.perform(new Map());
  }

  /** Watches the user property for changes in the user id */
  @task
  *userWatcher(profileDataLoadedMap, currentUser = null) {
    while (true) {
      currentUser = yield waitForProperty(this, 'args.user', (user) => user?.id !== currentUser?.id);

      if (currentUser) {
        this.manageProfile.perform(currentUser.id, profileDataLoadedMap);
      }
    }
  }

  /** Loads the incoming user. */
  @restartableTask
  *manageProfile(userID, profileDataLoadedMap) {
    const cachedProfilePromise = profileDataLoadedMap.get(userID);

    if (Date.now() - cachedProfilePromise?.timestamp < SELLER_PROFILE__CACHE_TIMEOUT_MS) {
      return yield cachedProfilePromise;
    }

    const includes = [
      ...PROFILE_USER_INCLUDES_BUYER,
      ...PROFILE_USER_INCLUDES_SELLER,
    ];

    if (this.session.currentUser?.id === userID) {
      includes.push(...PROFILE_USER_INCLUDES_SELF);
    }

    // this is explicitly chained off of the promise so that even if the task is cancelled, this logic
    // will complete
    const loadUserPromise = this.userCommandService.getUser(userID, includes).catch((e) => {
      this.errorTracker.error(`manageProfile -- can not load user ${userID}`, this, e);

      if (profileDataLoadedMap.get(userID) === loadUserPromise) {
        profileDataLoadedMap.delete(userID)
      }

      throw e;
    });

    loadUserPromise.timestamp = Date.now();
    profileDataLoadedMap.set(userID, loadUserPromise);

    return yield loadUserPromise;
  }

  /**
   * @param {String} projectID
   *
   * Handles updates to the project query parameter.
   */
  @action
  handleProjectParamUpdate(projectID) {
    this.project = projectID || null;
  }
}

export default ProfileOverlayUserComponent;
